import { AxiosResponse } from 'axios';
import api from '../../../services/api';
import { ClientReference, ExpiryStockProductProps, getClientsProps } from './client-interface';
import { formatCpfCnpjForFilter } from '~/utils/format';

class ClientService {
  getAll({ fields = [], skip = 0, limit = 12, userExternalCode, ...rest }: getClientsProps) {
    return api.get('/client', {
      params: {
        skip,
        limit,
        fieldsToShow: fields.join(','),
        userExternalCode,
        queryString: rest?.queryString
      }
    });
  }

  getAllImported({ fields = [], skip = 0, limit = 12, userExternalCode, ...rest }: getClientsProps) {
    return api.get('/client/action', {
      params: {
        skip,
        limit,
        fieldsToShow: fields.join(','),
        userExternalCode,
        queryString: rest?.queryString
      }
    });
  }

  getQuery(
    { fields, skip, limit, queryString, userExternalCode, showRatting, engine, ...rest }: getClientsProps,
    opts?: any
  ) {
    const formatedQueryString = queryString ? formatCpfCnpjForFilter(queryString) : '';
    return api.get('/client', {
      params: {
        skip,
        limit,
        fieldsToShow: fields.join(','),
        queryString: formatedQueryString,
        userExternalCode,
        showRatting,
        engine,
        ...rest
      },
      ...opts
    });
  }

  getRowsCount({ fields = [], skip = 0, limit = 12, userExternalCode, ...rest }: getClientsProps) {
    return api.get('/client/count', {
      params: {
        skip,
        limit,
        fieldsToShow: fields.join(','),
        userExternalCode,
        queryString: rest?.queryString
      }
    });
  }

  getRowsCountAction({ fields = [], skip = 0, limit = 12, userExternalCode, ...rest }: getClientsProps) {
    return api.get('/client/action/count', {
      params: {
        skip,
        limit,
        fieldsToShow: fields.join(','),
        userExternalCode,
        queryString: rest?.queryString
      }
    });
  }

  getClient(id: any) {
    return api.get(`/client/${id}`);
  }

  getClientImported(id: any) {
    return api.get(`/client/action/${id}`);
  }

  deleteClient(id: any, client: any) {
    return api.put(`/client/action/${id}`, {
      params: {
        ...client,
        clientActionInactive: true
      }
    });
  }

  resendClient(id: string, payload: any) {
    return api.put(`/client/${id}`, {
      params: {
        ...payload
      }
    });
  }

  createClient(payload: any): Promise<AxiosResponse<any | null>> {
    return api.post('/client/', payload);
  }

  editClient(payload: any): Promise<AxiosResponse<any | null>> {
    return api.put('/client', payload, {
      params: {
        isRattingUpdate: true
      }
    });
  }

  blockReason(payload) {
    return api.patch('/client/blockReason', payload);
  }

  getClientsRoute() {
    return api.get('/offline/clients-route');
  }

  getClientLastOrder(payload) {
    return api.get(
      `/order/clientLastOrder?clientExternalCode=${payload?.clientExternalCode}&productExternalCode=${
        payload?.productExternalCode
      }&allTeam=${true}&useProductRestrictions=${true}`
    );
  }

  getAllRatings({ size = 10, limit = 100, clientExternalCode }: any) {
    return api.get('/client-rating', {
      params: {
        size,
        limit,
        clientExternalCode
      }
    });
  }

  saveRating(payload) {
    return api.post('/client-rating', payload);
  }

  saveResponseChecklist(payload) {
    return api.post('/questionary/response', payload);
  }

  getAllImages({ clientExternalCode }: { clientExternalCode: string }) {
    return api.get(`/client/pictures/${clientExternalCode}`);
  }

  getCoverImage({ clientExternalCode }: { clientExternalCode: string }) {
    return api.get(`/client/pictures/cover/${clientExternalCode}`);
  }

  saveImagesClient(clientExternalCode: string, files: FileList) {
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    return api.put(`/client/pictures/${clientExternalCode}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  deleteImage(imageUrl: string, clientExternalCode: string): Promise<AxiosResponse<any>> {
    return api.post(`/client/pictures/delete/${clientExternalCode}`, {
      pictureUrl: imageUrl
    });
  }

  getClientExpiryStockProducts(
    {
      productExternalCode,
      subsidiaryExternalCode,
      productExpiry
    }: {
      productExternalCode: string;
      subsidiaryExternalCode: string;
      productExpiry: number;
    },
    opts?: any
  ): Promise<AxiosResponse<ExpiryStockProductProps[] | null>> {
    return api.get('/client/expiry-stock', {
      params: { productExternalCode, subsidiaryExternalCode, productExpiry },
      ...opts
    });
  }

  getQuestionary() {
    return api.get(`/questionary/`);
  }

  getHistoricQuestionary({ clientExternalCode }: { clientExternalCode: string }) {
    return api.get(`/questionary/response?clientsExternalCodes=${clientExternalCode}`);
  }

  getCustomizableClientFields() {
    return api.get('/client/customizable-client-action/required-fields');
  }

  getClassifications() {
    return api.get('/client/classification');
  }

  saveCustomizableClientFields(payload: string[]) {
    return api.post('/client/customizable-client-action/required-fields', {
      requiredFields: payload
    });
  }

  uploadDocuments(files: any) {
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    return api.put(`/client/action/documents/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  getClientReferences(clientExternalCode: string): Promise<AxiosResponse<ClientReference[]>> {
    return api.get<ClientReference[]>('/client/reference', {
      params: {
        clientExternalCode
      }
    });
  }

  saveClientReferences(
    clientExternalCode: string,
    companyReference: string,
    contactReference: string,
    phoneReference: string
  ): Promise<AxiosResponse<ClientReference>> {
    return api.post<ClientReference>('/client/reference/action', {
      clientExternalCode,
      companyReference,
      contactReference,
      phoneReference
    });
  }
}

export default new ClientService();
